import React, { useEffect, useState } from 'react';
import AdminLayout from '../../components/layout/AdminLayout';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const PLAN_BADGE = {
  free:    { cls:'badge--gray',    label:'Free' },
  daily:   { cls:'badge--blue',    label:'Daily' },
  monthly: { cls:'badge--green',   label:'Monthly' },
  custom:  { cls:'badge--purple',  label:'Custom' },
};

const PLAN_PRESETS = [
  { plan:'free',    label:'Free',         contactLimit:10,  expiryDays:null, price:'₹0' },
  { plan:'daily',   label:'Daily',        contactLimit:50,  expiryDays:1,    price:'₹99' },
  { plan:'monthly', label:'Monthly',      contactLimit:100, expiryDays:30,   price:'₹499' },
  { plan:'custom',  label:'Custom',       contactLimit:null,expiryDays:null, price:'Manual' },
];

export default function SubscriptionsPage() {
  const [subs,     setSubs]     = useState([]);
  const [stats,    setStats]    = useState({});
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState('');
  const [planFilter,setPlanFilter] = useState('');
  const [page,     setPage]     = useState(1);
  const [total,    setTotal]    = useState(0);
  const [selected, setSelected] = useState(null); // user being edited
  const [saving,   setSaving]   = useState(false);
  const [editForm, setEditForm] = useState({
    plan:'free', contactLimit:10, expiryDays:'', notes:'', resetCount:false
  });

  useEffect(() => { loadStats(); }, []);
  useEffect(() => { load(); }, [search, planFilter, page]);

  const loadStats = async () => {
    try {
      const { data } = await api.get('/subscription/admin/stats');
      setStats(data.stats || {});
    } catch {}
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit:20 });
      if (search)     params.set('search', search);
      if (planFilter) params.set('plan', planFilter);
      const { data } = await api.get('/subscription/admin/all?' + params);
      setSubs(data.subscriptions || []);
      setTotal(data.total || 0);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  const openEdit = (sub) => {
    setSelected(sub);
    setEditForm({
      plan:         sub.plan,
      contactLimit: sub.contactLimit,
      expiryDays:   '',
      notes:        sub.notes || '',
      resetCount:   false,
    });
  };

  const applyPreset = (preset) => {
    setEditForm(p => ({
      ...p,
      plan:         preset.plan,
      contactLimit: preset.contactLimit || p.contactLimit,
      expiryDays:   preset.expiryDays || '',
    }));
  };

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const { data } = await api.put('/subscription/admin/' + selected.userId._id, editForm);
      setSubs(p => p.map(s => s._id === selected._id ? data.subscription : s));
      toast.success('Subscription updated ✅');
      setSelected(null);
      loadStats();
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
    finally { setSaving(false); }
  };

  const resetToFree = async (sub) => {
    if (!window.confirm(`Reset ${sub.userId?.name || sub.userId?.mobile} to free plan?`)) return;
    try {
      await api.delete('/subscription/admin/' + sub.userId._id + '/reset');
      toast.success('Reset to free');
      load(); loadStats();
    } catch { toast.error('Error'); }
  };

  const isExpired = (sub) => sub.expiryDate && new Date() > new Date(sub.expiryDate);
  const remaining = (sub) => Math.max(0, sub.contactLimit - sub.contactsUsed);

  return (
    <AdminLayout title="Subscription Management">

      {/* Stats */}
      <div className="stat-grid" style={{ gridTemplateColumns:'repeat(4,1fr)', marginBottom:20 }}>
        {[
          { label:'Total users',   value:stats.total||0,   icon:'👥', cls:'stat-card--blue' },
          { label:'Monthly plan',  value:stats.monthly||0, icon:'📅', cls:'stat-card--green' },
          { label:'Daily plan',    value:stats.daily||0,   icon:'🌅', cls:'stat-card--amber' },
          { label:'Custom/Free',   value:(stats.custom||0)+(stats.free||0), icon:'🎁', cls:'stat-card--gray' },
        ].map(s => (
          <div key={s.label} className={`stat-card ${s.cls}`}>
            <div className="stat-card__icon">{s.icon}</div>
            <div className="stat-card__value">{s.value}</div>
            <div className="stat-card__label">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex-between" style={{ marginBottom:12 }}>
        <div style={{ display:'flex', gap:8 }}>
          <input className="input" placeholder="Search name or mobile..." value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }} style={{ width:220 }} />
          <select className="input" value={planFilter} onChange={e => { setPlanFilter(e.target.value); setPage(1); }} style={{ width:140 }}>
            <option value="">All plans</option>
            <option value="free">Free</option>
            <option value="daily">Daily</option>
            <option value="monthly">Monthly</option>
            <option value="custom">Custom</option>
          </select>
        </div>
        <span style={{ fontSize:12, color:'#6C757D' }}>{total} users</span>
      </div>

      {/* Table */}
      <div className="card">
        {loading ? <div className="page-loader"><span className="spinner spinner--md" /></div> : (
          <div className="table-wrap">
            <table>
              <thead><tr>
                <th>User</th><th>Plan</th><th>Contacts used</th>
                <th>Remaining</th><th>Expires</th><th>Notes</th><th>Actions</th>
              </tr></thead>
              <tbody>
                {subs.length === 0 ? (
                  <tr><td colSpan={7}>
                    <div className="empty-state">
                      <div className="empty-state__icon">💳</div>
                      <div className="empty-state__title">No subscriptions yet</div>
                      <div className="empty-state__desc">Users appear here once they reveal a contact</div>
                    </div>
                  </td></tr>
                ) : subs.map(sub => {
                  const pb  = PLAN_BADGE[sub.plan] || PLAN_BADGE.free;
                  const exp = isExpired(sub);
                  return (
                    <tr key={sub._id} style={{ opacity: !sub.isActive ? 0.5 : 1 }}>
                      <td>
                        <div style={{ fontWeight:600, fontSize:13 }}>{sub.userId?.name || '—'}</div>
                        <div style={{ fontSize:11, color:'#6C757D' }}>+91 {sub.userId?.mobile} · {sub.userId?.role}</div>
                      </td>
                      <td><span className={`badge ${pb.cls}`}>{pb.label}</span></td>
                      <td>
                        <div style={{ fontSize:13 }}>{sub.contactsUsed} / {sub.contactLimit}</div>
                        <div style={{ height:4, background:'#f0f0f0', borderRadius:2, marginTop:3, width:80 }}>
                          <div style={{ height:4, borderRadius:2, background: sub.contactsUsed/sub.contactLimit > 0.8 ? '#DC2626':'#1A6B3C', width:`${Math.min(100,(sub.contactsUsed/sub.contactLimit*100))}%` }} />
                        </div>
                      </td>
                      <td>
                        <span style={{ fontSize:13, fontWeight:600, color: remaining(sub)===0?'#DC2626':'#1A6B3C' }}>
                          {remaining(sub)} left
                        </span>
                      </td>
                      <td style={{ fontSize:12 }}>
                        {sub.expiryDate
                          ? <span style={{ color:exp?'#DC2626':'#1A6B3C' }}>
                              {exp ? '⚠️ Expired' : new Date(sub.expiryDate).toLocaleDateString('en-IN')}
                            </span>
                          : <span style={{ color:'#6C757D' }}>No expiry</span>}
                      </td>
                      <td style={{ fontSize:12, color:'#6C757D', maxWidth:120 }}>{sub.notes || '—'}</td>
                      <td>
                        <div style={{ display:'flex', gap:6 }}>
                          <button className="btn btn--primary btn--sm" onClick={() => openEdit(sub)}>✏️ Edit</button>
                          <button className="btn btn--outline btn--sm" onClick={() => resetToFree(sub)}>Reset</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {total > 20 && (
        <div className="pagination">
          <button className="btn btn--outline btn--sm" disabled={page===1} onClick={() => setPage(p=>p-1)}>← Prev</button>
          <span className="page-info">Page {page} of {Math.ceil(total/20)}</span>
          <button className="btn btn--outline btn--sm" disabled={page>=Math.ceil(total/20)} onClick={() => setPage(p=>p+1)}>Next →</button>
        </div>
      )}

      {/* Edit Modal */}
      {selected && (
        <div className="modal-overlay" onClick={() => setSelected(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth:500 }}>
            <div className="modal__header">
              <div className="modal__title">✏️ Edit Subscription — {selected.userId?.name || selected.userId?.mobile}</div>
              <button className="modal__close" onClick={() => setSelected(null)}>×</button>
            </div>
            <div className="modal__body">

              {/* Quick presets */}
              <div style={{ marginBottom:16 }}>
                <label className="label">Quick plan presets</label>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:6 }}>
                  {PLAN_PRESETS.map(preset => (
                    <button key={preset.plan} onClick={() => applyPreset(preset)}
                      style={{ padding:'8px 4px', borderRadius:8, border:`1.5px solid ${editForm.plan===preset.plan?'#1A6B3C':'#dee2e6'}`, background:editForm.plan===preset.plan?'#f0fdf4':'white', cursor:'pointer', textAlign:'center' }}>
                      <div style={{ fontSize:12, fontWeight:600, color:'#1a2e05' }}>{preset.label}</div>
                      <div style={{ fontSize:11, color:'#6C757D' }}>{preset.price}</div>
                      {preset.contactLimit && <div style={{ fontSize:10, color:'#1A6B3C' }}>{preset.contactLimit} contacts</div>}
                    </button>
                  ))}
                </div>
              </div>

              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                <div className="form-group">
                  <label className="label">Contact limit</label>
                  <input className="input" type="number" min={0}
                    value={editForm.contactLimit}
                    onChange={e => setEditForm(p=>({...p,contactLimit:parseInt(e.target.value)||0}))} />
                </div>
                <div className="form-group">
                  <label className="label">Expiry (days from now)</label>
                  <input className="input" type="number" min={0} placeholder="e.g. 30"
                    value={editForm.expiryDays}
                    onChange={e => setEditForm(p=>({...p,expiryDays:e.target.value}))} />
                  <div style={{ fontSize:11, color:'#6C757D', marginTop:3 }}>Leave empty to keep current expiry</div>
                </div>
              </div>

              <div className="form-group">
                <label className="label">Notes (payment info, reason etc.)</label>
                <input className="input" placeholder="e.g. Paid ₹499 via UPI on 19/05/2026"
                  value={editForm.notes}
                  onChange={e => setEditForm(p=>({...p,notes:e.target.value}))} />
              </div>

              <div style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 12px', background:'#FEF3C7', borderRadius:8, marginBottom:8 }}>
                <input type="checkbox" id="reset-count" checked={editForm.resetCount}
                  onChange={e => setEditForm(p=>({...p,resetCount:e.target.checked}))} />
                <label htmlFor="reset-count" style={{ fontSize:13, cursor:'pointer' }}>
                  ⚠️ Reset contact count to 0 (clears all revealed contacts)
                </label>
              </div>

              {/* Current status */}
              <div style={{ background:'#f8f9fa', borderRadius:8, padding:'10px 12px', fontSize:12, color:'#6C757D' }}>
                Current: <strong>{selected.contactsUsed}/{selected.contactLimit}</strong> contacts used ·
                {selected.expiryDate ? ` Expires ${new Date(selected.expiryDate).toLocaleDateString('en-IN')}` : ' No expiry'}
              </div>
            </div>
            <div className="modal__footer">
              <button className="btn btn--outline" onClick={() => setSelected(null)}>Cancel</button>
              <button className="btn btn--primary" onClick={save} disabled={saving}>
                {saving ? <span className="spinner spinner--sm spinner--white" /> : '✅ Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
